import BaseStore from 'customer-flows/stores/base-store'
import { context } from 'customer-flows/utils/core'
import { formatDynamicPrice, getCurrencySymbol, getCurrencyMessage, getPriceMoney } from 'customer-flows/utils/dynamic-pricing'
import { formatPrice } from 'customer-flows/utils/format-price'

export default class DynamicPricingStoreClass extends BaseStore {
  @context('availability:availabilityDates') availabilityDates: AvailabilityStore['availabilityDates']
  @context('availability:selectedDate') selectedDate: AvailabilityStore['selectedDate']
  @context('availability:selectedAvailabilityDate') selectedAvailabilityDate: AvailabilityStore['selectedAvailabilityDate']
  @context('activity:isActivity') isActivity: ActivityStore['isActivity']
  @context('activity:isRental') isRental: ActivityStore['isRental']
  @context('dynamic-pricing/rental:*') rentalDomain: RentalDynamicPricingStore
  @context('dynamic-pricing/activity:*') activityDomain: ActivityDynamicPricingStore

  private get domain(): RentalDynamicPricingStore | ActivityDynamicPricingStore {
    return this.isRental ? this.rentalDomain : this.activityDomain
  }

  get isEnabled(): boolean {
    return this.isActivity ?? false
  }

  // ---------------------------------------------------------------------------
  // Slot-level helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns the ticket to use for price display on the given time slot, respecting
   * the slot's whitelist.
   *
   * Behaviour is determined by the domain store:
   * - Activities: first ticket in the candidate list.
   * - Rentals: ticket with the lowest display price across all candidates.
   */
  getTicketForSlot(slot: AvailabilityTime): Ticket | undefined {
    return this.domain.getTicketForSlot(slot)
  }

  /**
   * Returns the raw Money value to display for the given time slot.
   *
   * Returns null when no price data is available for the slot's ticket.
   */
  getDisplayPriceForSlot(slot: AvailabilityTime): Money | null {
    const ticket = this.getTicketForSlot(slot)
    if (!ticket) return null

    const priceEntry = slot.prices?.find((p) => p.resourceOptionId === ticket.id)
    const pricing = this.domain.getPricingEntry(priceEntry)
    if (!pricing) return null

    return getPriceMoney(pricing)
  }

  /**
   * Returns the formatted price string for display on a time tile, including
   * the currency symbol prefix when applicable.
   *
   * Returns null when there is no price to display.
   */
  getFormattedPriceForSlot(slot: AvailabilityTime): string | null {
    const money = this.getDisplayPriceForSlot(slot)
    if (!money) return null

    const amount = parseFloat(money.amount)
    if (isNaN(amount)) return null

    const symbol = getCurrencySymbol(money.currency)
    const formatted = formatDynamicPrice(amount)

    return symbol ? `${symbol}${formatted}` : formatted
  }

  // ---------------------------------------------------------------------------
  // Selected date derived state
  // ---------------------------------------------------------------------------

  /**
   * Whether dynamic prices should be shown on time tiles for the selected date.
   *
   * Prices are shown only when at least two slots have distinct display prices,
   * computed per-slot using the first whitelisted selectable ticket. This
   * accounts for selectable_tickets where different slots can have a different
   * first-available ticket with a different price.
   */
  get shouldShowPricesForTimes(): boolean {
    if (!this.isEnabled) return false

    const slots = this.selectedAvailabilityDate?.availabilityTimes ?? []
    const prices = slots
      .map((slot) => this.getDisplayPriceForSlot(slot))
      .filter((p): p is Money => p !== null)
      .map((p) => p.amount)

    if (prices.length < 2) return false

    const first = prices[0]!
    return prices.some((p) => p !== first)
  }

  /**
   * The lowest price (as a raw numeric value) across slots for the selected date.
   * Used to highlight the cheapest slot in green.
   *
   * Returns null when prices are not being shown.
   */
  get lowestDisplayPrice(): number | null {
    if (!this.shouldShowPricesForTimes) return null

    const slots = this.selectedAvailabilityDate?.availabilityTimes ?? []
    const amounts = slots
      .map((slot) => this.getDisplayPriceForSlot(slot))
      .filter((p): p is Money => p !== null)
      .map((p) => parseFloat(p.amount))
      .filter((n) => !isNaN(n))

    if (amounts.length === 0) return null

    return Math.min(...amounts)
  }

  /**
   * Returns whether the given Money value represents the lowest price for the
   * currently selected date. Used to drive the green "lowest price" highlight.
   */
  isLowestPrice(money: Money): boolean {
    const lowest = this.lowestDisplayPrice
    if (lowest === null) return false

    return parseFloat(money.amount) === lowest
  }

  // ---------------------------------------------------------------------------
  // Calendar date-level helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns the price to display for a calendar date tile.
   *
   * Behaviour is determined by the domain store:
   * - Activities: first entry of priceRange.
   * - Rentals: cheapest entry across priceRange.
   *
   * Returns null if no price data is available for the date.
   */
  getDisplayPriceForDate(date: AvailabilityDate): Money | null {
    const range = date.priceRange
    if (!range?.length) return null
    return this.domain.getDisplayPriceForDate(range)
  }

  /**
   * Returns the formatted price string for display on a calendar date tile,
   * including the currency symbol prefix when applicable.
   *
   * Returns null when there is no price to display.
   */
  getFormattedPriceForDate(date: AvailabilityDate): string | null {
    const money = this.getDisplayPriceForDate(date)
    if (!money) return null

    const amount = parseFloat(money.amount)
    if (isNaN(amount)) return null

    const symbol = getCurrencySymbol(money.currency)
    const formatted = formatDynamicPrice(amount)

    return symbol ? `${symbol}${formatted}` : formatted
  }

  /**
   * Whether price labels should be shown on calendar date tiles.
   *
   * Prices are shown only when at least two dates have different prices.
   * When all dates share the same price, no prices are shown.
   */
  get shouldShowPricesOnCalendar(): boolean {
    if (!this.isEnabled) return false

    const dates = this.availabilityDates.value ?? []
    if (dates.length === 0) return false

    const amounts = dates
      .map((d) => this.getDisplayPriceForDate(d))
      .filter((p): p is Money => p !== null)
      .map((p) => p.amount)

    if (amounts.length < 2) return false

    const first = amounts[0]
    return amounts.some((a) => a !== first)
  }

  /**
   * The lowest display price (as a raw numeric value) across all calendar dates.
   * Used to highlight the cheapest date in green.
   *
   * Returns null when prices are not being shown on the calendar.
   */
  get lowestCalendarPrice(): number | null {
    if (!this.shouldShowPricesOnCalendar) return null

    const amounts = (this.availabilityDates.value ?? [])
      .map((d) => this.getDisplayPriceForDate(d))
      .filter((p): p is Money => p !== null)
      .map((p) => parseFloat(p.amount))
      .filter((n) => !isNaN(n))

    if (amounts.length === 0) return null

    return Math.min(...amounts)
  }

  /**
   * Returns whether the given date has the lowest price across the current
   * availability period. Used to drive the green "lowest price" highlight on
   * calendar date tiles.
   */
  isLowestPriceDate(date: AvailabilityDate): boolean {
    const lowest = this.lowestCalendarPrice
    if (lowest === null) return false

    const price = this.getDisplayPriceForDate(date)
    if (!price) return false

    return parseFloat(price.amount) === lowest
  }

  // ---------------------------------------------------------------------------
  // Month-level from-price helpers (activity summary hero tag)
  // ---------------------------------------------------------------------------

  /**
   * The lowest price across all availability dates in the current month.
   * Uses priceRange[0] from each date, which the backend provides as the
   * correct from-price (already the cheapest option for that date).
   *
   * Returns null when no price data is available.
   */
  get fromPriceForMonth(): Money | null {
    const dates = this.availabilityDates.value ?? []
    if (dates.length === 0) return null

    let lowest: Money | null = null

    for (const date of dates) {
      const price = date.priceRange?.[0]
      if (!price) continue

      const amount = parseFloat(price.amount)
      if (isNaN(amount)) continue
      if (lowest === null || amount < parseFloat(lowest.amount)) lowest = price
    }

    return lowest
  }

  /**
   * Formatted "from price" string for the current month, including currency symbol.
   * Shows the exact lowest price with decimals (no rounding), matching the price
   * shown in the ticket selector (activities) and duration picker (rentals).
   * Returns null when no price is available.
   */
  get formattedFromPriceForMonth(): string | null {
    const money = this.fromPriceForMonth
    if (!money) return null

    const amount = parseFloat(money.amount)
    if (isNaN(amount)) return null

    return formatPrice(amount, money.currency)
  }

  // ---------------------------------------------------------------------------
  // Currency helpers (delegated to utilities, surfaced here for convenience)
  // ---------------------------------------------------------------------------

  getCurrencySymbol(currency: string): string | null {
    return getCurrencySymbol(currency)
  }

  getCurrencyMessage(currency: string): string | null {
    return getCurrencyMessage(currency)
  }
}

declare global {
  export type DynamicPricingStore = DynamicPricingStoreClass

  export interface StoreRegistry {
    'dynamic-pricing': DynamicPricingStore
  }
}
